import { isDefined } from "@clipboard-health/util-ts";
import { get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import {
  type QueryFunctionContext,
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";

import {
  type GetPlacementApplicationsResponse,
  paginatedPlacementApplicationsResponseSchema,
  type PlacementApplicationsQuery,
  placementApplicationsQuerySchema,
} from "../types/fetchPlacementApplications.schema";

export function fetchPlacementApplicationsApiUrl(workerId: string) {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/workers/${workerId}/placement-applications`;
}

export interface FetchPlacementApplicationsParams {
  workerId: string;
  filter?: PlacementApplicationsQuery["filter"];
  sort?: "asc" | "desc";
  pageSize?: number;
}

function extractCursorFromNextLink(nextUrl: string): string | undefined {
  try {
    const url = new URL(nextUrl);
    return url.searchParams.get("page[cursor]") ?? undefined;
  } catch {
    return undefined;
  }
}

export function useFetchPlacementApplications(
  params: FetchPlacementApplicationsParams,
  options: UseInfiniteQueryOptions<GetPlacementApplicationsResponse, AxiosError> = {}
) {
  const { workerId, filter, sort, pageSize = 20 } = params;

  return useInfiniteQuery({
    queryKey: [fetchPlacementApplicationsApiUrl(workerId), filter, sort],
    queryFn: async ({
      pageParam: pageParameter = undefined,
    }: QueryFunctionContext<QueryKey, string | undefined>) => {
      const nonEmptyFilter = Object.fromEntries(
        Object.entries(filter ?? {}).filter(
          ([_, value]) => isDefined(value) && (typeof value !== "string" || value !== "")
        )
      );

      const requestUrl = fetchPlacementApplicationsApiUrl(workerId);
      const queryParams = {
        page: {
          size: pageSize,
          cursor: pageParameter,
        },
        filter: nonEmptyFilter,
        ...(isDefined(sort) ? { sort } : {}),
      };

      const response = await get({
        url: requestUrl,
        queryParams,
        responseSchema: paginatedPlacementApplicationsResponseSchema,
        requestSchema: placementApplicationsQuerySchema,
      });

      return response.data;
    },
    getNextPageParam: (lastPage) => {
      const nextUrl = lastPage.links?.next;
      if (!nextUrl) {
        return undefined;
      }

      return extractCursorFromNextLink(nextUrl);
    },
    ...options,
  });
}
